import React, { useState, useEffect, useRef } from 'react';
import {
  Stack,
  Flex,
  Button,
  Column,
  Copy,
  Row,
  Squish,
  SvgPencil,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { UploadStatus } from 'src/components/CSVUploadTracking/CSVUploadTracking.utils';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import {
  EnterpriseSetupTable,
  IEnterpriseSetupTableRowProps,
} from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';
import {
  IDiner,
  LONG_SEARCH_DELAY,
  useDinerSearch,
} from 'src/utilities/useSearchHooks';
import { ManageEnterpriseEmployeeModal } from 'src/components/ManageEnterpriseEmployeeModal/ManageEnterpriseEmployeeModal';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { sdk } from 'src/utilities/sdk';
import { isFunction } from 'src/utilities/is-function';

interface IEnterpriseEmployeesTableProps {
  corpId: string;
  hasItemsCb: (hasAddedEmployees: boolean) => void;
  currentUploadStatus: UploadStatus;
  isCsvFileAccepted: boolean;
}
export function EnterpriseEmployeesTable({
  corpId,
  hasItemsCb,
  currentUploadStatus,
  isCsvFileAccepted,
}: IEnterpriseEmployeesTableProps): JSX.Element {
  const setupTableRef = useRef(null);

  useEffect(() => {
    if (
      setupTableRef.current &&
      isFunction(setupTableRef.current.updateSearch) &&
      isCsvFileAccepted &&
      (currentUploadStatus.isSuccessful || currentUploadStatus.hasError)
    ) {
      setupTableRef.current.updateSearch();
    }
  }, [currentUploadStatus, isCsvFileAccepted]);

  return (
    <EnterpriseSetupTable<IDiner>
      ref={setupTableRef}
      corpId={corpId}
      searchPlaceholder="Search by name or email"
      noResultsMessage="You don't have any employees yet"
      hasItemsCb={hasItemsCb}
      searchResource={useDinerSearch}
      headerColumns={[
        { columnName: 'Name', columnWidth: 4 },
        { columnName: 'Email', columnWidth: 6 },
        { columnWidth: 2 },
      ]}
      rowElementRender={(
        employee,
        isEditing,
        toggleEditState,
        updateSearch
      ) => (
        <EnterpriseSetupEmployeesTableRow
          employee={employee}
          isEditing={isEditing}
          toggleEditState={toggleEditState}
          updateSearch={updateSearch}
        />
      )}
      addButtonText="Add employee"
      footerAddItemRender={(toggleAddState, updateSearch) => (
        <ManageEnterpriseEmployeeModal
          corpId={corpId}
          closeModal={toggleAddState}
          onSuccess={() => {
            updateSearch({ page: 1 });
            toggleAddState();
          }}
        />
      )}
    />
  );
}

interface IEnterpriseSetupEmployeesTableRowProps
  extends IEnterpriseSetupTableRowProps {
  employee: IDiner;
}

export function EnterpriseSetupEmployeesTableRow({
  employee,
  isEditing,
  toggleEditState,
  updateSearch,
}: IEnterpriseSetupEmployeesTableRowProps): JSX.Element {
  const { corpId } = useEnterpriseSignupStatus();
  const [showRemoveConfirmationModal, setShowRemoveConfirmationModal] =
    useState(false);
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [fullEmployeeName] = useState(
    `${employee.first_name} ${employee.last_name}`
  );

  useEffect(() => {
    async function handleAsyncStatus() {
      if (asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
        await onSuccessfulRemoval();
      } else if (asyncTaskStatus === AsyncTaskStatus.ERROR) {
        onFailedRemoval();
      }

      setIsDeleteInProgress(asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS);
    }

    handleAsyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  async function onSuccessfulRemoval(): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        pushSuccess(`${fullEmployeeName} was removed`);
        setShowRemoveConfirmationModal(false);
        updateSearch({ page: 1 });
        resolve();
      }, LONG_SEARCH_DELAY);
    });
  }

  function onFailedRemoval() {
    pushError(`${fullEmployeeName} was not removed. Try again.`);
  }

  async function deleteEmployee(): Promise<void> {
    try {
      setIsDeleteInProgress(true);
      await startAsyncTask(() => {
        return sdk.http.delete(
          `/corp-admin/${corpId}/diners/v2/${employee.id}`
        );
      });
    } catch (error) {
      onFailedRemoval();
      setIsDeleteInProgress(false);
    }
  }

  if (isEditing) {
    return (
      <ManageEnterpriseEmployeeModal
        employeeId={employee.id}
        corpId={corpId}
        closeModal={() => toggleEditState()}
        onSuccess={() => {
          updateSearch({ page: 1 });
          toggleEditState();
        }}
      />
    );
  }

  return (
    <>
      <Row
        gutter={0}
        data-testid={`employee-row-${employee.id}`}
        key={employee.id}
      >
        <Column gutter={0} phone={4}>
          <Squish inset="sm" className="ellipsisOverflow">
            <Copy variant="body">{fullEmployeeName}</Copy>
          </Squish>
        </Column>
        <Column gutter={0} phone={6}>
          <Squish inset="sm" className="ellipsisOverflow">
            <Copy variant="body">{employee.email}</Copy>
          </Squish>
        </Column>
        <Column gutter={0} phone={2}>
          <Flex alignItems="center" justifyContent="flex-end">
            <Stack x="x-sm">
              <Button
                variant="tertiary"
                onClick={() => toggleEditState(employee.id)}
              >
                <SvgPencil aria-label="Edit employee" />
              </Button>
            </Stack>
            <Button
              variant="tertiary"
              onClick={() => setShowRemoveConfirmationModal(true)}
            >
              <SvgX aria-label="Remove employee" />
            </Button>
          </Flex>
        </Column>
      </Row>
      {showRemoveConfirmationModal && (
        <SimpleDialog
          id={employee.id}
          title={`Remove "${fullEmployeeName}"?`}
          description="You’re about to remove this employee."
          primaryActionText="Yes, remove"
          isPrimaryButtonLoading={isDeleteInProgress}
          primaryActionFlavor="danger"
          onPrimaryActionClick={deleteEmployee}
          disablePrimaryActionButton={isDeleteInProgress}
          secondaryActionText="Cancel"
          secondaryButtonVariant="secondary"
          onSecondaryActionClick={() => setShowRemoveConfirmationModal(false)}
          disableSecondaryActionButton={isDeleteInProgress}
          buttonsDirection="row"
        />
      )}
    </>
  );
}
